import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AccountErrorCodes } from '@app/translations';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  ActiveDevice,
  AlertTypes,
  BaseWidgetProperties,
  OpenBankingProvider,
  PISPDepositStatusEnum,
  UiZoneWidgetAttributeConfig,
  defaultPispDepositFlowRedirectionConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, firstValueFrom, take, tap } from 'rxjs';
import { SetFormAction, UpdateFormDataAction } from '../../../actions/form-submission.action';
import { ConfigService } from '../../../services/config-service/config-service.service';
import { WebFrontendConfig } from '../../../services/config-service/config.type';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import {
  PispDepositRequest,
  PispRecurringDepositConfig,
} from '../../../services/savings-account-service/savings-account.type';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { ProjectSettingsState } from '../../../state/project-settings.state';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { waitForFlutterWebView } from '../../flutter.utils';
import { buildActionNavigationTargetUrl, handleRedirection } from '../cta-redirection.utils';
import { SetPispDepositInitiateResponse } from '../../../actions/savings-account.action';
import { RoleState } from '../../../state/role.state';
import { getOpenBankingProvider } from '../../project-settings.utils';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';

let flutterWebViewInstance: any;

function validateDepositFormAmount(injector: Injector, attri: UiZoneWidgetAttributeConfig): number | null {
  const store = injector.get(Store);
  const activeSavingsAccountDetails = store.selectSnapshot(SavingsAccountState.getCurrentAccount);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const buttonAction = attri.widgetProperties.buttonActionType as string;
  //Set button to disabled state
  ctaButtonSignalService.setSignal({ [buttonAction]: false });
  const workflowFormsService = injector.get(WorkflowFormsService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const router = injector.get(Router);

  // get all forms values in one object
  const allPageFormValues: { [key: string]: any } = workflowFormsService.getAllFormValuesFlattened();
  const currentPageFormValues: { [key: string]: any } = allPageFormValues[FormID.DEPOSIT_FORM];
  if (!currentPageFormValues || !currentPageFormValues['amount']) {
    console.error('Please enter an amount');
    alertHandlerService.showAlertFn(AlertTypes.ERROR, 'Please enter an amount');
    ctaButtonSignalService.setSignal({ [buttonAction]: true });
    return null;
  }

  if (!activeSavingsAccountDetails?.accountId) {
    console.error('Please select an account');
    alertHandlerService.showAlertFn(AlertTypes.ERROR, 'Please select an account');
    ctaButtonSignalService.setSignal({ [buttonAction]: true });
    return null;
  }

  const amount = Number(currentPageFormValues['amount']);
  const availableBalance = Number(activeSavingsAccountDetails.availableBalance);

  if (isNaN(amount) || amount === 0) {
    alertHandlerService.showAlertFn(AlertTypes.WARNING, 'Please enter a valid amount to proceed');
    ctaButtonSignalService.setSignal({ [buttonAction]: true });
    return null;
  }

  if (isNaN(availableBalance)) {
    findAndShowAlertFromCode(store, router, alertHandlerService, [AccountErrorCodes.AMOUNT_NOT_VALID]);
    ctaButtonSignalService.setSignal({ [buttonAction]: true });
    return null;
  }

  ctaButtonSignalService.setSignal({ [buttonAction]: true });
  return amount;
}

export function appendBillingRequestIdToUrl(url: string, billingRequestId: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}billing_request_id=${billingRequestId}`;
}

// Read by the institution picker widget (pisp-banks-listing-widget) to decide which institution
// catalog to show - 'vrp' opts into the VRP-scoped list instead of the default PIS-scoped one.
export function appendFeatureToUrl(url: string, feature: 'pis' | 'vrp'): string {
  return `${url}${url.includes('?') ? '&' : '?'}feature=${feature}`;
}

function mapRecurringDepositFormValues(injector: Injector): PispRecurringDepositConfig | null {
  const workflowFormsService = injector.get(WorkflowFormsService);
  const allPageFormValues: { [key: string]: any } = workflowFormsService.getAllFormValuesFlattened();
  const recurringFormValues: { [key: string]: any } = allPageFormValues[FormID.PISP_RECURRING_DEPOSIT_FORM];

  if (!recurringFormValues || !recurringFormValues['frequency']) {
    return null;
  }

  return {
    frequency: recurringFormValues['frequency'],
    collectionDay:
      recurringFormValues['collectionDay'] !== undefined && recurringFormValues['collectionDay'] !== null
        ? Number(recurringFormValues['collectionDay'])
        : undefined,
    endDate: recurringFormValues['endDate'] || undefined,
  };
}

export async function reviewPispDeposit(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const store = injector.get(Store);
  const nominatedAccountDetails = store.selectSnapshot(SavingsAccountState.getNominatedAccountDetails);

  const amount = validateDepositFormAmount(injector, attri);
  if (amount === null) {
    return;
  }

  const workflowFormsService = injector.get(WorkflowFormsService);
  const allPageFormValues: { [key: string]: any } = workflowFormsService.getAllFormValuesFlattened();
  const currentPageFormValues: { [key: string]: any } = allPageFormValues[FormID.DEPOSIT_FORM];

  let recurring: PispRecurringDepositConfig | undefined;
  if (currentPageFormValues?.['depositType'] === 'recurring') {
    const alertHandlerService = injector.get(AlertHandlerService);
    recurring = mapRecurringDepositFormValues(injector) ?? undefined;
    if (!recurring) {
      console.error('Recurring deposit selected but recurring form values are missing or incomplete');
      alertHandlerService.showAlertFn(AlertTypes.ERROR, 'Please complete the recurring payment details');
      return;
    }
    // carry the recurring config forward so initiatePispDeposit can attach it to the payload
    store.dispatch(new UpdateFormDataAction({ formData: { recurring } }));
  }

  // If the pisp instituion id is already set on the nominated account, it means user has already gone through the flow once and is now can be just sent to the review page otherwise we need them to pick the bank once.
  // TODO look at extracting this from gocardless as well for uses who have done this already and it's been guessed??
  if (!nominatedAccountDetails?.pispInstitutionId) {
    // feature tells the institution picker widget which catalog to show - VRP-capable
    // institutions for a recurring deposit, the standard PIS-scoped list otherwise.
    const targetUrl = buildActionNavigationTargetUrl(
      attri.widgetProperties,
      injector,
      ActionButtonOutcomesType.FAILURE,
    );
    injector
      .get(Router)
      .navigateByUrl(appendFeatureToUrl(targetUrl, recurring ? 'vrp' : 'pis'), { replaceUrl: true });
    return;
  }

  const configService = injector.get(ConfigService);
  const isCustomPaymentPage = !!configService.config.goCardlessEnabledCustomPaymentPages;

  // Always mint this (and so always run the institution capability check inside it) - even for
  // the hosted-flow path, which doesn't need the resulting billing request for anything. Without
  // this, a VRP-incapable institution was never caught until GC's own hosted flow discovered it
  // couldn't attach the institution and silently fell back to re-prompting the user to pick one
  // inside its own overlay - confusing in general, and nonsensical for a customer whose deposit
  // is already tied to a specific real linked account (confirmed by testing against sandbox).
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const buttonAction = attri.widgetProperties.buttonActionType as string;
  ctaButtonSignalService.setSignal({ [buttonAction]: false });
  const savingsAccountService = injector.get(SavingsAccountService);
  const accountId = store.selectSnapshot(SavingsAccountState.getCurrentAccount)?.accountId as string;
  try {
    const { billingRequestId } = await firstValueFrom(
      savingsAccountService.createPispBillingRequest(accountId, { amount, isRecurring: !!recurring }),
    );
    const targetUrl = buildActionNavigationTargetUrl(
      attri.widgetProperties,
      injector,
      ActionButtonOutcomesType.SUCCESS,
    );
    // Only the custom payment page flow's compliance widget needs this id (see createMandate's
    // confirm step, which mints its own fresh billing request regardless either way) - the
    // hosted-flow path has nothing to do with the one just minted here.
    const finalUrl = isCustomPaymentPage
      ? appendBillingRequestIdToUrl(targetUrl, billingRequestId)
      : targetUrl;
    injector.get(Router).navigateByUrl(finalUrl, { replaceUrl: true });
  } catch (error: any) {
    console.error(`Error creating pisp billing request: ${error.error.message}`, error);
    const alertCodes = (error as { error?: { alertCodes?: string[] } })?.error?.alertCodes;
    const alertHandlerService = injector.get(AlertHandlerService);
    if (alertCodes?.includes(AccountErrorCodes.INSTITUTION_ACTION_NOT_SUPPORTED)) {
      // Selected institution doesn't support this deposit type (e.g. no VRP support) - send
      // the user to whatever page this button's "Institution Not Supported" outcome is
      // configured with, rather than just showing a toast.
      handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.ALTERNATE_1, true);
    } else if (alertCodes?.length) {
      findAndShowAlertFromCode(store, injector.get(Router), alertHandlerService, alertCodes);
    } else {
      alertHandlerService.showAlertFn(AlertTypes.ERROR, 'Unable to prepare your deposit. Please try again.');
    }
  } finally {
    ctaButtonSignalService.setSignal({ [buttonAction]: true });
  }
}

export async function initiatePispDeposit(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const buttonAction = attri.widgetProperties.buttonActionType as string;
  //Set button to disabled state
  ctaButtonSignalService.setSignal({ [buttonAction]: false });
  const store = injector.get(Store);
  const activeSavingsAccountDetails = store.selectSnapshot(SavingsAccountState.getCurrentAccount);
  const savingsAccountService = injector.get(SavingsAccountService);
  const configService = injector.get(ConfigService);
  const deviceDetector = injector.get(DeviceDetectorService);

  // get flutter web view instance if the device is mobile

  const flutterWebView = await waitForFlutterWebView(1000);
  if (flutterWebView === null) {
    // only log an error when instance is not found on mobile/tablet devices - on desktop
    // there's no Flutter web view to find, so its absence there is expected, not an error
    if (deviceDetector.isMobile() || deviceDetector.isTablet()) {
      console.error('Flutter web view instance not found');
    }
  } else {
    flutterWebViewInstance = flutterWebView;
  }

  const isMobileOrTabletOrApp =
    deviceDetector.isMobile() || deviceDetector.isTablet() || flutterWebViewInstance !== undefined;
  // Wait for config before proceeding
  const configValues = await firstValueFrom(configService.getConfig().pipe(take(1)));

  const amount = validateDepositFormAmount(injector, attri);
  if (!activeSavingsAccountDetails?.accountId || amount === null) {
    return;
  }

  const openBankingProvider = getOpenBankingProvider(store);

  const redirectAndExitUrl = '/pisp-deposit-status';
  // use the custom redirect url if the project has it set and the device is mobile or tablet, otherwise use the default redirect url
  const redirectUri: string =
    configService.config.goCardlessEnabledCustomPaymentPages &&
    configService.config.goCardlessCustomPaymentRedirectUri &&
    isMobileOrTabletOrApp
      ? configService.config.goCardlessCustomPaymentRedirectUri
      : configValues.api_endpoints.default + redirectAndExitUrl + `?from=${openBankingProvider}`;

  const workflowFormsService = injector.get(WorkflowFormsService);
  const allPageFormValues: { [key: string]: any } = workflowFormsService.getAllFormValuesFlattened();
  const currentPageFormValues: { [key: string]: any } = allPageFormValues[FormID.DEPOSIT_FORM];
  const recurring: PispRecurringDepositConfig | undefined =
    currentPageFormValues?.['depositType'] === 'recurring'
      ? (mapRecurringDepositFormValues(injector) ?? undefined)
      : undefined;

  const payload: PispDepositRequest = {
    amount: amount,
    redirectUri: redirectUri,
    exitUri: redirectUri,
    isCustomPaymentPage: configValues.goCardlessEnabledCustomPaymentPages ?? undefined,
    // billing request minted at review time (see reviewPispDeposit) — the compliance widget on
    // this page rendered against it, so confirm the deposit on the same one
    billingRequestId: new URLSearchParams(window.location.search).get('billing_request_id') ?? undefined,
    ...(recurring && { recurring }),
  };
  savingsAccountService
    .initiatePispDeposit(activeSavingsAccountDetails.accountId, payload)
    .pipe(
      take(1),
      tap((response) => {
        store.dispatch(new SetFormAction({ type: 'pispDeposit', formData: response }));

        // Only real gocardless gets its proprietary drop-in widget below - every other provider
        // (including any future one) returns a bank authorisationUrl to redirect/deep-link to.
        if (
          configService.config.goCardlessEnabledCustomPaymentPages ||
          openBankingProvider !== OpenBankingProvider.GOCARDLESS
        ) {
          store.dispatch(new SetPispDepositInitiateResponse(response));

          // Try detect if in the native app or web browser and handle redirection accordingly
          if (isMobileOrTabletOrApp) {
            handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.ALTERNATE_2, true);
            setTimeout(() => {
              flutterWebViewInstance?.callHandler('redirectionComplete', []);
              // navigate to the authorisation url for custom payment page flow
              window.open(response.authorisationUrl ?? '', '_blank');
            }, 1000);
          } else if (openBankingProvider === OpenBankingProvider.NATWEST_PAYIT) {
            // NatWest Payit doesn't use QR codes - redirect the current tab straight to the authorisation URL on desktop
            window.location.href = response.authorisationUrl ?? '';
          } else {
            // For desktop we want to show QR code as the primary then give another link to open banking app in a new tab, for mobile we can directly launch the banking app via. deep linking without showing the qr code
            handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.ALTERNATE_1, true);
          }
          // TODO start polling for the payment status and redirect user accordingly for custom payment page flow - getting back via. webhook + websockets would be better
          //TODO On desktop we want to show the qr code to allow continuing on your mobile desktop so they can open their banking app and complete the payment, on mobile we can directly launch the banking app via. deep linking without showing the qr code
          // We should show pending screen as we begin the polling and then redirect to success or failure screen based on the payment status as the billing request resolves

          // navigate to new polling page which will show pending screen and handle redirection based on payment status
        } else {
          launchGoCardlessDropinHandler(
            response.paymentId,
            activeSavingsAccountDetails.accountId,
            injector,
            attri.widgetProperties,
            ctaButtonSignalService,
            configValues,
          );
        }
      }),
      catchError((error) => {
        console.error('Error initiating pisp deposit', error);
        ctaButtonSignalService.setSignal({ [buttonAction]: true });
        throw error;
      }),
    )
    .subscribe();
}

function launchGoCardlessDropinHandler(
  billingRequestFlowID: string,
  accountId: string,
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
  ctaButtonSignalService: CtaButtonSignalService,
  configValues: WebFrontendConfig,
) {
  const buttonAction = widgetProperties.buttonActionType as string;
  const store = injector.get(Store);
  const deviceDetector = injector.get(DeviceDetectorService);
  const GoCardlessDropin = (window as Window & typeof globalThis & { GoCardlessDropin: any })
    .GoCardlessDropin;
  const handler = GoCardlessDropin.create({
    billingRequestFlowID: billingRequestFlowID,
    environment: configValues?.goCardlessEnvironment ?? 'sandbox', // either read from config or sandbox
    onSuccess: () => {
      checkPispDepositStatusAndRedirectUser(injector, accountId, widgetProperties, ctaButtonSignalService);
      handler.exit();
    },
    onExit: (error: any) => {
      if (error !== null) console.error('Error in go cardless dropin', error);
      store.dispatch(new UpdateFormDataAction({ formData: { pispDepositStatus: 'cancelled' } }));
      flutterWebViewInstance?.callHandler('redirectionComplete', []);
      // enable the button again
      ctaButtonSignalService.setSignal({ [buttonAction]: true });
      handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
    },
  });
  flutterWebViewInstance?.callHandler('InitiateExternalUrl', []);
  handler.open();
  // enable the button on mobile app because it is disabled on mobile app even after flow completion
  if (!deviceDetector.isDesktop()) ctaButtonSignalService.setSignal({ [buttonAction]: true });
}

function checkPispDepositStatusAndRedirectUser(
  injector: Injector,
  accountId: string,
  widgetProperties: BaseWidgetProperties,
  ctaButtonSignalService: CtaButtonSignalService,
) {
  const store = injector.get(Store);
  const savingsAccountService = injector.get(SavingsAccountService);
  const router = injector.get(Router);
  const deviceDetector = injector.get(DeviceDetectorService);
  const activeDevice = deviceDetector.deviceType as ActiveDevice;
  const pispDepositId = store.selectSnapshot(FormActionState.getFormActionState).response?.formData?.id;
  const buttonAction = widgetProperties.buttonActionType as string;
  savingsAccountService
    .checkPispDepositStatus(pispDepositId, accountId)
    .pipe(
      take(1),
      tap((response) => {
        // enable the button again
        ctaButtonSignalService.setSignal({ [buttonAction]: true });
        store.dispatch(new UpdateFormDataAction({ formData: { pispDepositStatus: response.status } }));
        flutterWebViewInstance?.callHandler('redirectionComplete', []);
        redirectUserToDepositStatusPage(response.status, store, router, activeDevice);
      }),
      catchError((error) => {
        console.error('Error checking pisp deposit status', error);
        ctaButtonSignalService.setSignal({ [buttonAction]: true });
        throw error;
      }),
    )
    .subscribe();
}

export function redirectUserToDepositStatusPage(
  status: PISPDepositStatusEnum,
  store: Store,
  router: Router,
  activeDevice: ActiveDevice,
) {
  // take redirection config from project settings state or fallback to default
  const redirectionConfig =
    store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
      ?.pispDepositFlowRedirectionConfig || defaultPispDepositFlowRedirectionConfig;

  // Prefer the device held in role state over the raw device-detector value passed in: role state stores
  // the device view the workflow is actually rendered for (a tablet is resolved down to 'mobile' when
  // Tablet View is disabled for the project), so it's the one the redirection config is keyed by.
  const roleStateActiveDevice = store.selectSnapshot(RoleState.getRole)?.device as ActiveDevice;
  if (roleStateActiveDevice) {
    activeDevice = roleStateActiveDevice;
  }
  const redirectionUrl = redirectionConfig?.[activeDevice][status];
  if (!redirectionUrl) {
    console.error('Redirection URL not found for PISP deposit status and device', status, activeDevice);
    window.location.href = '/zones/landing/home';
    return;
  }
  router.navigateByUrl(redirectionUrl, { replaceUrl: true });
}
